import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { Messages } from "../../../configuration/event-messages.constants";
import { EventAggregator } from "aurelia-event-aggregator";
import { Guid } from "../../../utilities/guid";
import { bindable, ViewResources } from "aurelia-templating";
import { Container, transient } from "aurelia-dependency-injection";
import { autoinject } from "aurelia-dependency-injection";
import { TemplatingEngine } from "aurelia-templating";
import { DashboardClientService } from "../../../services/dashboard/dashboard-client.service";
import {
    Dashboard,
    DashboardWidget,
    GetDashboardsResponse,
    PersistDashboardResponse,
    DashboardWidgetCoordinates
} from "../../../models/dashboard/dashboard.models";
import { type Widget, type WidgetConfigurationBroker, GlobalSearchWidgetModel, BaseQueryWidget } from "../../../widgets/models";
import "jqueryui";
import "jquery-ui-touch-punch";
import { I18N } from "aurelia-i18n";
import * as Enumerable from "linq";
import { DialogServiceConstants } from "../../../constants/basics/dialog.constants";
import { NotificationClientService, NotificationType } from '../../../services/basics/notification-client.service';
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { IBaseWidget } from "../../../widgets/base-widget";
import { computedFrom, Disposable } from "aurelia-binding";
import { QueryFilter } from "../../../models/queries/queries.models";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../constants/authentication/base-claims";
import { NotifyPropertyChangedBase, Reflection } from "../../../utilities/reflection";
// import 'gridstack/dist/gridstack.min.css';
import { GridStack } from 'gridstack';

@transient()
@autoinject()
export class DashboardView extends NotifyPropertyChangedBase {

    private _subscriptions: Disposable[] = [];
    private _globalSearchWidgetId: number;
    private _selectedDashboardIdKey: string = "selected_dashboard_id";
    private _loadWidgetsPromiseCount: number = 0;

    public gridElement: JQuery;
    public grid: any;
    public _allWidgets: Widget[];
    public editingElementConfiguration: JQuery;
    public currentDashboard: Dashboard;
    public menu: any = {};
    public serializedData: DashboardWidget[];
    public _scrollWithWidgetHandler: { enable: () => void, disable: () => void };
    public itemToDelete;
    public loadingDashboard: boolean;
    public gridstackOptions: any = {
        alwaysShowResizeHandle: false,
        animate: true,
        auto: true,
        cellHeight: 160,
        draggable: { handle: ".widget", scroll: true, appendTo: "app-body" },
        handle: ".widget",
        height: 160,
        float: false,
        itemClass: "grid-stack-item",
        minWidth: 320,
        placeholderClass: "grid-stack-placeholder",
        resizable: { autoHide: true, handles: "se" },
        staticGrid: false,
        verticalMargin: 10,
        width: this.getGridWidth()
    };

    public allLoadedDashboardsWidgets: { [dashboardId: string]: DashboardWidget[]; } = {};
    public isCloning: boolean = false;
    public dashboards: Dashboard[];
    public canShareDashboard: boolean = false;
    public isRemovingDashboard: boolean;
    public isMenuOpen: boolean = false;
    public showMenu: boolean = false;
    public tabsElement: HTMLDivElement;
    public dashboardMenuItems: { [key: string]: { isVisible: boolean } } = {};

    @bindable
    public isManageDashboards: boolean = false;

    @bindable
    public showTabs: boolean = true;

    @bindable
    public globalDashboardId: number;

    @bindable
    public addNewVisible: boolean;

    @bindable
    public isEditing: boolean = false;

    @bindable
    public readonly: boolean = undefined;

    @bindable
    public selectedWidget: Widget;

    @bindable
    public isEditConfiguration: boolean;

    @bindable
    public searchText: string;

    @computedFrom(
        Reflection.getPropertyPath<DashboardView>(x => x._allWidgets),
        Reflection.getPropertyPath<DashboardView>(x => x.searchText),
    )
    public get widgets(): Widget[] {

        let list: Widget[] = [];
        if (this.searchText) {
            for (const item of this._allWidgets) {
                if (item.title.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1) {
                    list.push(item);
                }
            }
        } else {
            list = this._allWidgets;
        }
        return list;
    }

    @bindable
    public step: number = 1;

    @bindable
    public currentConfiguration: string;

    @bindable
    public widgetConfigBroker: WidgetConfigurationBroker = {};

    @computedFrom(
        Reflection.getPropertyPath<DashboardView>(x => x.currentDashboard),
        Reflection.getPropertyPath<DashboardView>(x => x.loadingDashboard),
        Reflection.getPropertyPath<DashboardView>(x => x.changes.allLoadedDashboardsWidgets)
    )
    public get showDashboardPlaceholder(): boolean {
        return (!this.currentDashboard
            || !this.allLoadedDashboardsWidgets[this.currentDashboard.id]
            || this.allLoadedDashboardsWidgets[this.currentDashboard.id].length === 0)
            && !this.loadingDashboard;
    }

    @computedFrom(
        Reflection.getPropertyPath<DashboardView>(x => x.currentDashboard),
        Reflection.getPropertyPath<DashboardView>(x => x.readonly),
    )
    public get canEdit(): boolean {
        if (this.readonly !== undefined && this.readonly !== null) {
            return !this.readonly;
        }
        const result = this.currentDashboard && !this.currentDashboard.isReadonly;
        return result;
    }
    
    @computedFrom(
        Reflection.getPropertyPath<DashboardView>(x => x.canEdit),
        Reflection.getPropertyPath<DashboardView>(x => x.showDashboardPlaceholder),
        Reflection.getPropertyPath<DashboardView>(x => x.isEditing)
    )
    public get shareClass() {
        if (this.canEdit && !this.showDashboardPlaceholder) {
            return "move-more-left";
        }
        else if (!this.showDashboardPlaceholder || this.isEditing) {
            return "move-left";
        }
        else {
            return '';
        }
    }

    public constructor(public templatingEngine: TemplatingEngine, private notificationService: NotificationClientService,
        private dialogClient: DialogClientService, public dashboardClientService: DashboardClientService, public resources: ViewResources, private i18n: I18N,
        private eventAggregator: EventAggregator, public container: Container, private cacheClient: CacheClientService, private userClientService: UserClientService,
        private userClaims: UserClaimsService) {
        super();
        this.canShareDashboard = this.userClaims.hasPermission(BaseClaims.AssignDashboards);
    }

    public getGridWidth(): number {
        return 12;
    }

    public selectWidget(model: Widget, modelFunc: () => any = null): void {
        this.selectedWidget = model;
        this.widgetConfigBroker = {};
        this.widgetConfigBroker.widgetModel = modelFunc;
        this.widgetConfigBroker.dashboardId = this.currentDashboard.dashboardId;
        this.widgetConfigBroker.parameter = this.getParameterForConfiguration(model);
        this.currentConfiguration = this.getConfigurationPath(model.internalName);

        const entityTypeUris = this.getDashboardWidgetsEntityTypeUris();
        this.widgetConfigBroker.dashboardEntityTypeUris = entityTypeUris
    }

    getDashboardWidgetsEntityTypeUris() {
        if (!this.currentDashboard) {
            return;
        }
        // get unique list of entity type uris in dashboard, filter out global search widget as they do not have an entity type
        const entityTypeUris = this.allLoadedDashboardsWidgets[this.currentDashboard.id].filter(x => (!x.widget || x.widget && x.widget.internalName != 'global-search-widget')).map(x => {
            //get index of entityTypeUri
            const indexOfEntityTypeUri = x.settings.indexOf("entityTypeUri");
            //get substring starting from entityTypeUri
            const fromEntityTypeString = x.settings.substr(indexOfEntityTypeUri);
            //get value of entityTypeUri
            const entityTypeUri = fromEntityTypeString.substring(fromEntityTypeString.indexOf("\":\"") + 3, fromEntityTypeString.indexOf("\","));
            return entityTypeUri
        }).filter((value, index, array) => array.indexOf(value) === index);

        return entityTypeUris;
    }

    public getParameterForConfiguration(model: Widget): any {
        return this.getBasePath() + model.internalName;
    }

    public getConfigurationPath(internalName: string): string {
        return "../../../widgets/" + internalName + "-configuration";
    }

    public detached() {

        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];

        this.userClientService.unsubscribeOnUserUpdate(this.loadDashboard);

        window.removeEventListener('resize', this.reloadTabs);

        if (this._scrollWithWidgetHandler) {
            this._scrollWithWidgetHandler.disable();
        }
    }

    public attached(): void {
        this.loadingDashboard = true;

        GridStack.prototype.cellWidth = () => {
            return 160;
        };

        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];

        this._subscriptions.push(
            this.eventAggregator.subscribe(Messages.documentClick, () => {
                for (const y of Object.keys(this.menu)) {
                    this.menu[y] = false;
                }
            }));

        this.gridElement = $("#grid-stack");

        this.gridstackOptions.cellHeight = 160;
        this.grid = GridStack.init(this.gridstackOptions);
        // this.grid = gridstack.data("gridstack");
        this.grid.disable();

        this.userClientService.subscribeOnUserUpdate(this.loadDashboard);
        this.loadDashboard();
        window.addEventListener('resize', this.reloadTabs);
    }

    loadDashboard = () => {
        if (!this.isManageDashboards) {
            this.loadUserDashboards();
        }
        else {
            this.loadGlobalDashboards();
        }
    }

    protected reloadDashboard() {

        this.loadingDashboard = true;
        this.load(this.currentDashboard);
        this.isEditing = false;
    }

    public loadGlobalDashboards() {
        this.dashboardClientService.getGlobalDashboards().then(x => {
            const dashboards = Enumerable.from(x.dashboards);
            const selectedDashboard = dashboards.first(d => d.dashboardId == this.globalDashboardId);
            this.load(selectedDashboard);
        })
    }

    public loadUserDashboards() {
        this.getDashboardsForUser().then(dashboardsResult => {
            if (dashboardsResult.dashboards) {
                this.dashboards = dashboardsResult.dashboards;
            }
            if (this.dashboards && this.dashboards.length > 0) {
                this.cacheClient.get(this._selectedDashboardIdKey, CacheLocation.LocalStorage, false).then(dashboardid => {
                    const dashboardEnumerable = Enumerable.from(this.dashboards);
                    const selectedDashboard = dashboardEnumerable.firstOrDefault(z => z.dashboardId == dashboardid);
                    let index = this.dashboards.indexOf(selectedDashboard);

                    if (index < 0) {
                        index = 0;
                    }

                    this.load(this.dashboards[index]);
                    this.reloadTabs();
                }).catch(() => {
                    this.load(this.dashboards[0]);
                    this.reloadTabs();
                });
            } else {
                this.load({
                    id: 0,
                    createdDate: new Date(),
                    dashboardId: 0,
                    name: "Default",
                    identifier: Guid.generateGuid()
                });
                this.showMenu = false;
            }
        });
    }

    public editDashboard(): void {
        if (this.canEdit) {
            this.serialize();
            this.grid.enable();
            this.isEditing = true;
            if (!this._scrollWithWidgetHandler) {
                this._scrollWithWidgetHandler = this.getScrollWithWidgetHandler();
            }
            this._scrollWithWidgetHandler.enable();
        }
    }

    public toggleEditMode() {
        this.isEditing = !this.isEditing;
    }

    public canDeactivate(): boolean {
        if (this.isEditing) {
            return confirm(this.i18n.tr("dashboard-not-saved"));
        } else {
            return true;
        }
    }

    public doneEditing(): void {
        this.grid.disable();
        this.isEditing = false;
        this.save();
        if (this._scrollWithWidgetHandler) {
            this._scrollWithWidgetHandler.disable();
        }
    }

    public cancelEditingInner(shouldLoad: boolean = true): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.grid.disable();
            this.isEditing = false;
            if (shouldLoad) {
                this.load(this.currentDashboard).then(resolve).catch(reject);
            } else {
                resolve();
            }
        })
    }

    public cancelEditing(load: boolean = true): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            this.searchText = null;
            if (this._scrollWithWidgetHandler) {
                this._scrollWithWidgetHandler.disable();
            }

            if (this._allWidgets) {
                this.cancelEditingInner(load).then(resolve).catch(reject);
            } else {
                this.loadWidgets().then(() => {
                    this.cancelEditingInner(load).then(resolve).catch(reject);
                });
            }
        });
    }

    public configure(): void {
        if (this.selectedWidget.hasConfiguration) {
            this.step++;
        } else {
            this.saveWidget();
        }
    }

    public addWidget(): void {

        if (this._allWidgets) {
            this.goToAddStep();
        } else {
            this.loadWidgets().then(() => {
                this.goToAddStep();
            });
        }
    }

    public goToAddStep(): void {
        this.isEditing = true;
        this.isEditConfiguration = false;
        this.step = 1;
        this.addNewVisible = true;
    }

    public hideAdd(): void {
        this.selectedWidget = null;
        this.addNewVisible = false;
        this.step = 1;
        this.searchText = null;
    }

    public saveWidgetConfiguration(): void {

        const promiseFunc = this.widgetConfigBroker.onBeforeSave ? this.widgetConfigBroker.onBeforeSave : () => { return new Promise<void>((resolve) => resolve()) };

        promiseFunc().then(() => {
            const node = this.editingElementConfiguration.data("_gridstack_node");
            const dashboardWidgetId = this.editingElementConfiguration.data("dashboardWidgetId");
            this.grid.removeWidget(this.editingElementConfiguration.get(0));

            const config = this.widgetConfigBroker.getModel();
            this.createWidget(dashboardWidgetId, this.selectedWidget, this.stringify(config), node.x, node.y, node.width, node.height, true);

            this.grid.disable();
            this.isEditing = false;
            this.dashboardClientService.updateDashboardWidget(dashboardWidgetId, this.stringify(config));
        }).catch(() => {
            this.notificationService.show("Error", "Cannot save widget", NotificationType.Error);
        });
    }

    public createWidget(dashboardWidgetId: number, widget: Widget, config: string, x: number, y: number, w: number, h: number, enhance: boolean, internalName: string = null, defaultGlobalFilters: QueryFilter[] = null): JQuery {

        const parsedConfig = this.parse(config);
        parsedConfig.height = h;
        parsedConfig.width = w;
        parsedConfig.defaultGlobalFilters = defaultGlobalFilters;
        parsedConfig.dashboardWidgetId = dashboardWidgetId;

        const element: JQuery = this.grid.addWidget(this.getContainer(internalName ? internalName : widget.internalName,
            false, parsedConfig, widget), x, y, w, h, false);

        element.data({
            dashboardWidgetId,
            settings: config,
            internalName: internalName ? internalName : widget.internalName,
            widgetId: widget.widgetId
        });

        if (enhance) {
            const bindingContext = {
                config: parsedConfig,
                removeItem: this.removeItem,
                openMenu: this.openMenu,
                menu: this.menu,
                editConfiguration: this.editConfiguration,
                showCloneDashboardWidgetDialog: this.showCloneDashboardWidgetDialog
            };
            this.templatingEngine.enhance({
                element: element.get(0),
                bindingContext: bindingContext,
                resources: this.resources
            });
            this.onEnhanced(element);
        }
        this.hideAdd();

        return element;
    }

    public onEnhanced(element: JQuery): void {
        if (this.isCloning) {
            const pos = element[0].offsetTop;
            $(".gridstack").animate({ scrollTop: pos }, "500");
        }
        return;
    }

    public removeItem = (e, widgetCompose: any): void => {
        const viewModel = widgetCompose.currentViewModel;
        this.isRemovingDashboard = false;
        this.itemToDelete = e.target;
        this.dialogClient.show(DialogServiceConstants.URLs.DeleteModal, {
            delete: () => this.delete(viewModel)
        });
    }

    public showCloneDashboardWidgetDialog = (e): void => {
        const element: JQuery = $($(e.target).closest(".grid-stack-item").get(0));
        const dashboardWidgetId: number = element.data("dashboardWidgetId");
        const dashboardWidgetWidth: number = element.data("width");
        const dashboards: Dashboard[] = this.dashboards ? this.dashboards : [this.currentDashboard];

        const model: any = {
            dashboards: dashboards,
            currentDashboardWidgets: this.allLoadedDashboardsWidgets[this.currentDashboard.dashboardId],
            clone: (dashboardId: number, isGlobal: boolean, coords: DashboardWidgetCoordinates) => this.cloneWidget(dashboardId, isGlobal, dashboardWidgetId, coords),
            currentDashboardId: this.currentDashboard.dashboardId,
            width: dashboardWidgetWidth ? dashboardWidgetWidth : 3
        }

        this.dialogClient.show(DialogServiceConstants.URLs.CloneDashboardWidgetDialog, model);
    }

    public delete(viewModel: IBaseWidget): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.isRemovingDashboard) {
                this.deleteDashboard().then(resolve).catch(reject);
            }
            else {

                const func = () => {
                    this.dialogClient.hide();
                    this.grid.removeWidget($(this.itemToDelete).closest(".grid-stack-item").get(0));
                    return this.save();
                };

                if (viewModel && viewModel.beforeDelete) {
                    viewModel.beforeDelete().then(() => {
                        func().then(resolve).catch(reject);
                    }).catch(reject);
                } else {
                    func().then(resolve).catch(reject);
                }
            }
        });
    }

    public editConfiguration = (e): void => {

        if (this._allWidgets) {
            this.goToEditConfiguration(e);
        } else {
            this.loadWidgets().then(() => {
                this.goToEditConfiguration(e);
            });
        }
    }

    public goToEditConfiguration(e): void {
        const element: JQuery = $($(e.target).closest(".grid-stack-item").get(0));
        const config: any = element.data("settings");
        const viewmodel: string = element.data("internalName");

        for (const item of this._allWidgets) {
            if (item.internalName === viewmodel) {
                this.selectedWidget = item;
                break;
            }
        }

        this.selectWidget(this.selectedWidget, () => this.parse(config));
        this.isEditConfiguration = true;
        this.editingElementConfiguration = element;
        this.step = 2;
        this.addNewVisible = true;
    }

    public saveWidget(): void {

        const promiseFunc = this.widgetConfigBroker.onBeforeSave ? this.widgetConfigBroker.onBeforeSave : () => { return new Promise<void>((resolve) => resolve()) };

        promiseFunc().then(() => {
            const config = this.selectedWidget.hasConfiguration ? this.widgetConfigBroker.getModel() : {};
            const createdWidget = this.createWidget(0, this.selectedWidget, this.stringify(config), 0, 0, 3, 2, true);

            setTimeout(() => {
                this.save().then(() => {
                    const latestWidget = this.allLoadedDashboardsWidgets[this.currentDashboard.id].length > 0
                        ? Enumerable.from(this.allLoadedDashboardsWidgets[this.currentDashboard.id]).orderBy(x => x.dashboardWidgetId).last()
                        : null;
                    createdWidget.data("dashboardWidgetId", latestWidget.id);
                });
            });
        }).catch(() => {
            this.notificationService.show("Error", "Cannot save widget", NotificationType.Error);
        });
    }

    public save(): Promise<void> {
        return new Promise<void>((resolve) => {
            const widgets = $(".widget-host").toArray();
            for (const widget of widgets) {
                const vm = (<any>widget).au.controller.viewModel.currentViewModel;
                if (this.shouldSaveConfig() && vm) {
                    const config = this.stringify(vm.model);
                    const element: JQuery = $($(widget).closest(".grid-stack-item").get(0));
                    element.data({
                        settings: config
                    });
                }
            }

            this.serialize();

            const dashboardWidgets = this.serializedData;
            this.currentConfiguration = null;
            this.selectedWidget = null;
            this.persistDashboard(this.currentDashboard, dashboardWidgets).then(x => {
                if (this.currentDashboard.dashboardId <= 0) {
                    this.currentDashboard = x.dashboard;
                    this.dashboards.push(x.dashboard);
                    this.reloadTabs()
                }
                this.dashboardClientService.getWidgetsForDashboard(this.currentDashboard.dashboardId).then(response => {
                    this.allLoadedDashboardsWidgets[this.currentDashboard.dashboardId] = response.dashboardWidgets;
                    this.onPropertyChanged<DashboardView>(x => x.allLoadedDashboardsWidgets);
                    resolve();
                });
            })
        });
    }

    public shouldSaveConfig(): boolean {
        return false;
    }

    public removeDashboard() {
        this.isRemovingDashboard = true;
        this.dialogClient.show(DialogServiceConstants.URLs.DeleteModal, this);
    }

    deleteDashboard() {
        return new Promise<void>((resolve, reject) => {
            this.dashboardClientService.deleteUserDashboard(this.currentDashboard.dashboardId).then(x => {
                if (x.isSuccess) {
                    this.notificationService.show(this.i18n.tr('successfully-deleted-text'), this.i18n.tr('dashboard-has-been-successfully-deleted-text'), NotificationType.Success);
                    this.loadUserDashboards();
                }
                else {
                    this.notificationService.show(this.i18n.tr('warning') + '!', this.i18n.tr('dashboard-cannot-be-deleted-in-use-text'), NotificationType.Warning);
                }
                this.dialogClient.hide();
                this.isRemovingDashboard = false;
                resolve();
            }).catch(() => {

                this.dialogClient.hide();
                this.isRemovingDashboard = false;
                reject();
            });
        })
    }

    public openMenu = (guid: string, e: any): void => {
        e.preventDefault();
        e.stopPropagation();
        let self;

        for (const x of Object.keys(this.menu)) {
            if (this.menu[guid] === this.menu[x] && this.menu[guid] === true) {
                self = true;
            }
            this.menu[x] = false;
        }

        //If Self is already Open.. Don't re-open the dropdown
        if (!self) {
            this.menu[guid] = true;
        }
    }

    public shareDashboard() {
        if (this.currentDashboard && this.canShareDashboard) {
            this.dialogClient.show(DialogServiceConstants.URLs.ShareDashboard, {
                dashboard: this.currentDashboard,
            })
        }
    }

    public getContainer(viewModel: string, forNew: boolean, config: any, widget: Widget): string {
        let html = "";
        html += "<div><div class=\"widget\"> ";
        const guid = Guid.generateGuid().replace(/-/g, "");
        const id = new Date().getTime() + Math.random();
        config.menuId = `widget-menu-dropdown-${id}`;
        const viewModelRef = `ref_${guid}`;
        config.showMenu = this.canEdit;
        if (!forNew) {

            //Widget Menu
            html += "<div if.bind=\"config.showMenu\" class=\"widget-menu row justify-content-center\" click.delegate=\"openMenu('" + guid + "', $event)\"><div class=\"widget-menu-dots\"></div></div>";

            //Menu Dropdown
            html += "<div id=\"widget-menu-dropdown-" + id + "\" class=\"widget-menu-dropdown ${menu['" + guid + "'] ? 'visible':'' } animation-zoomin-top-right \">";

            if (widget.hasConfiguration && this.canEdit) {
                //Configure
                html += "<div class=\"menu-item configure row\" click.delegate=\"editConfiguration($event)\"><i class=\"menu-item-icon material-icons\">build</i><span class=\"menu-item-text\">Configure</span></div>";
            }

            if (this.canEdit) {
                //Remove
                html += `<div class="menu-item remove row" click.delegate="removeItem($event,${viewModelRef})"><i class="menu-item-icon material-icons">clear</i><span class="menu-item-text">Remove</span></div>`;

                //Clone
                html += "<div class=\"menu-item clone row\" click.delegate=\"showCloneDashboardWidgetDialog($event)\"><i class=\"menu-item-icon material-icons\">filter_none</i><span class=\"menu-item-text\">Clone</span></div>";
            }

            //Closing Tags
            html += "</div>";
        }

        const identifier = "widget-host-" + Guid.generateGuid();
        const vm = this.getBasePath() + viewModel;
        html += "<compose class=\"widget-host " + identifier + "\" view-model=\"" + vm + "\" model.bind=\"config\" compose.ref='" + viewModelRef + "'></compose>";
        html += "</div></div>";

        return html;
    }

    public getBasePath(): string {
        return "src/widgets/";
    }

    public getModel(identifier: string, viewmodel: string, config: string): any {
        return config;
    }

    public load(dashboard: Dashboard): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.loadingDashboard = true;
            this._loadWidgetsPromiseCount++;
            const localLoadWidgetsPromiseCount = this._loadWidgetsPromiseCount;
            this.grid.removeAll();

            const loadWidgets = () => {
                if (localLoadWidgetsPromiseCount != this._loadWidgetsPromiseCount) {
                    reject();
                    return;
                }

                this.currentDashboard = dashboard;
                
                const widgets = Enumerable.from(this.allLoadedDashboardsWidgets[dashboard.id]).orderBy(x => x.y).toArray();
                const defaultGlobalFilters = this.getGlobalFilters(widgets);

                for (const node of widgets) {
                    node.widget = node.widget ? node.widget : Enumerable.from(this._allWidgets).first(x => x.widgetId === node.widgetId);
                    const internalName = node.widget ? node.widget.internalName : Enumerable.from(this._allWidgets).first(x => x.widgetId === node.widgetId).internalName;
                    this.createWidget(node.dashboardWidgetId, node.widget, node.settings, node.x, node.y, node.width, node.height, true, internalName, defaultGlobalFilters);
                }
                this.grid.disable();
                this.loadingDashboard = false;
                resolve();
            };

            const loadWidgetsOperation = () => {
                if (localLoadWidgetsPromiseCount != this._loadWidgetsPromiseCount) {
                    reject();
                    return;
                }
                if (this.allLoadedDashboardsWidgets[dashboard.id] == null || this.allLoadedDashboardsWidgets[dashboard.id].length <= 0) {
                    this.dashboardClientService.getWidgetsForDashboard(dashboard.dashboardId).then(x => {
                        this.allLoadedDashboardsWidgets[dashboard.id] = x.dashboardWidgets;
                        this.onPropertyChanged<DashboardView>(x => x.allLoadedDashboardsWidgets);
                        loadWidgets();
                    });
                }
                else {
                    loadWidgets();
                }
            }

            if (this.widgets && this._allWidgets) {
                loadWidgetsOperation();
            }
            else {
                this.loadWidgets().then(() => {
                    loadWidgetsOperation();
                });
            }
        });
    }

    getGlobalSearchWidgetId() {
        if (this.widgets && !this._globalSearchWidgetId) {
            const globalSearchWidget = this.widgets.filter(w => w && w.internalName == 'global-search-widget');

            this._globalSearchWidgetId = globalSearchWidget && globalSearchWidget.length >= 1 ? globalSearchWidget[0].widgetId : -1;
        }

        return this._globalSearchWidgetId;
    }

    private getGlobalFilters(widgets: DashboardWidget[]): QueryFilter[] {
        const globalSearchWidgets = widgets.filter(w => w && (w.widget && w.widget.internalName == 'global-search-widget') || (w.widgetId == this.getGlobalSearchWidgetId()));
        const parsedWidgetSettings = globalSearchWidgets.map(w => <GlobalSearchWidgetModel>this.parse(w.settings))

        const globalFilters = Enumerable.from(parsedWidgetSettings).selectMany(x => x.exposedFilterProperties).selectMany(x => x.filters).toArray();

        return globalFilters;
    }

    protected persistDashboard(currentDashboard: Dashboard, widgets: DashboardWidget[]): Promise<PersistDashboardResponse> {
        return this.dashboardClientService.persistDashboard(currentDashboard, widgets, this.isManageDashboards);
    }

    protected stringify(obj: any): string {
        return JSON.stringify(obj);
    }

    protected parse(obj: string): BaseQueryWidget {
        return JSON.parse(obj) as BaseQueryWidget;
    }

    protected serialize() {
        const result: DashboardWidget[] = [];
        const elements: HTMLElement[] = $("#grid-stack > .grid-stack-item").toArray();

        for (const el of elements) {
            const jel = $(el);

            const node = jel.data("_gridstack_node");
            result.push({
                id: jel.data("dashboardWidgetId"),
                dashboardWidgetId: jel.data("dashboardWidgetId"),
                dashboardId: this.currentDashboard.dashboardId,
                widgetId: jel.data("widgetId"),
                settings: jel.data("settings"),
                x: node.x,
                y: node.y,
                width: node.width,
                height: node.height
            });
        }

        this.serializedData = result;
    }

    protected loadWidgets(): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            if (this._allWidgets && this._allWidgets.length > 0) {
                resolve();
                return;
            }

            this.dashboardClientService.getWidgets().then(x => {
                this._allWidgets = x.widgets;
                resolve();
            }).catch(reject);
        });
    }

    protected getDashboardsForUser(): Promise<GetDashboardsResponse> {
        return this.dashboardClientService.getDashboardsForUser();
    }

    public onDashboardSelected(event, dashboard: Dashboard) {
        if (event) {
            event.stopPropagation();
        }
        if (dashboard) {
            this.currentDashboard = null;
            this.grid.removeAll();
            this.cancelEditing(false).then(() => {
                this.load(dashboard).then(() => {
                    this.cacheClient.put(this._selectedDashboardIdKey, dashboard.id);
                }).catch(() => { });
            }).catch(() => { });

            this.isMenuOpen = false;
        }
    }

    public onAddNewDashboard() {
        this.dialogClient.show(DialogServiceConstants.URLs.AddNewDashboard, this);
    }

    public onEditDashboards() {
        this.dialogClient.show(DialogServiceConstants.URLs.EditDashboards, this);
    }

    public cloneWidget(dashboardId: number, isGlobal: boolean, dashboardWidgetId: number, coords: DashboardWidgetCoordinates) {
        this.dashboardClientService.cloneDashboardWidget(dashboardWidgetId, coords, dashboardId)
            .then(x => {
                if (x.isSuccess) {
                    const dashboardToBeClonedOn: Dashboard = this.dashboards ? this.dashboards.filter(x => x.dashboardId == dashboardId)[0] : this.currentDashboard;
                    const dashboardWidgets: DashboardWidget[] = dashboardToBeClonedOn ? this.allLoadedDashboardsWidgets[dashboardToBeClonedOn.id] : null;
                    //adding widget to currently selected dashboard
                    if (dashboardId == this.currentDashboard.dashboardId) {
                        const dashboardWidget: DashboardWidget = x.dashboardWidget;
                        this.createWidget(dashboardWidget.dashboardWidgetId, dashboardWidget.widget, dashboardWidget.settings, dashboardWidget.x, dashboardWidget.y,
                            dashboardWidget.width, dashboardWidget.height, true);
                        this.allLoadedDashboardsWidgets[dashboardToBeClonedOn.id].push(dashboardWidget);
                    }
                    else if (!isGlobal && dashboardWidgets && dashboardWidgets.length > 0) {
                        //adding widget to one of currently available dashboards(the one selected in the dialog)
                        dashboardWidgets.push(x.dashboardWidget);
                    }

                    this.notificationService.show(this.i18n.tr('successful-cloning'), this.i18n.tr('successful-cloning-widget-description'), NotificationType.Success);
                }
                else {
                    this.notificationService.show(this.i18n.tr('widget-not-found-text'), this.i18n.tr('widget-not-found-description'), NotificationType.Error);
                }
            })
            .catch(() => {
                this.notificationService.show(this.i18n.tr('error'), this.i18n.tr('widget-clone-error-text'), NotificationType.Error);
            });
    }

    private getScrollWithWidgetHandler(): { enable: () => void, disable: () => void; } {
        const container = $(".gridstack");
        let selectedWidget;
        let mouseY;
        let widgetOffsetY;
        const gridStackItemSelector = ".grid-stack-item";
        const itemContent = $(gridStackItemSelector);

        const gridMouseMove = (event) => {
            mouseY = event.clientY;
        };

        const gridScrolling = () => {
            if (selectedWidget) {
                onScroll();
            }
        };

        const gridItemMouseDown = (event) => {
            widgetOffsetY = event.offsetY;
            selectedWidget = $(event.currentTarget).closest(".grid-stack-item").get(0);
            const width = selectedWidget.clientWidth;
            $(selectedWidget).css("position", "relative");
            $(selectedWidget).css("width", width + "px");
            container.on("scroll", gridScrolling);
        };

        const gridItemMouseUp = () => {
            $(selectedWidget).css("position", "absolute");
            $(selectedWidget).css("width", "");
            container.off("scroll", gridScrolling);
            selectedWidget = null;
        };

        const onScroll = () => {
            const containerScrollTop = container[0].scrollTop;

            $(selectedWidget).animate(
                { top: containerScrollTop + (mouseY ? mouseY : 0) - widgetOffsetY - container.offset().top },
                0
            );
        };

        const enable = () => {
            disable();
            container.on("mousemove", gridMouseMove);
            itemContent.on("mousedown", gridItemMouseDown);
            itemContent.on("mouseup", gridItemMouseUp);
        }

        const disable = () => {
            container.off("mousemove", gridMouseMove);
            itemContent.off("mousedown", gridItemMouseDown);
            itemContent.off("mouseup", gridItemMouseUp);
            selectedWidget = null;
        };

        return { disable, enable };
    }

    public openDashboardMenu($event) {
        if ($event) {
            $event.stopPropagation();
        }

        this.isMenuOpen = !this.isMenuOpen;
    }

    public onDashboardMenuBlur() {
        this.isMenuOpen = false;
    }

    public resetTabVisibility() {
        const dashboardIds = Enumerable.from(this.dashboards).select(x => x.dashboardId).toArray();
        dashboardIds.forEach(x => {
            if (!this.dashboardMenuItems[x]) {
                this.dashboardMenuItems[x] = { isVisible: false };
            } else {
                this.dashboardMenuItems[x].isVisible = false;
            }
        })
    }

    public reloadTabs = () => {
        setTimeout(() => {
            this.dashboardsChanged();
        }, 120);
    }

    public dashboardsChanged() {
        if (!(this.tabsElement instanceof HTMLElement)) {
            return;
        }
        const tabs = Array.prototype.slice.call(document.querySelectorAll('div[data-dashboard-id]')) as HTMLElement[];
        this.showMenu = false;
        const rootOffsetWidth = this.tabsElement.offsetWidth;
        const rootOffsetLeft = this.tabsElement.offsetLeft;
        let invisibleOffsetLeft = 0;

        this.resetTabVisibility();
        const dashboardIdsToShowInMenu = [];
        setTimeout(() => {
            for (let i = 0; i < tabs.length; i++) {
                const tab = tabs[i];
                const dashboardId = tab.getAttribute('data-dashboard-id');
                if (!(tab instanceof HTMLElement)) {
                    continue;
                }

                if (this.dashboardMenuItems[dashboardId].isVisible) {
                    continue;
                }

                if (tab.offsetLeft + tab.offsetWidth - (rootOffsetLeft + invisibleOffsetLeft) < rootOffsetWidth) {
                    continue;
                }

                if (dashboardId) {
                    dashboardIdsToShowInMenu.push(dashboardId);
                    invisibleOffsetLeft += tab.offsetWidth;
                }
            }

            dashboardIdsToShowInMenu.forEach(dashboardId => {
                this.dashboardMenuItems[dashboardId].isVisible = true;
            });
            this.showMenu = Object.keys(this.dashboardMenuItems).some(key => this.dashboardMenuItems[key].isVisible);
        }, 120);
    }

    public cleanTabsArray(deletedDashboards: Dashboard[]) {
        deletedDashboards.forEach(x => {
            this.dashboardMenuItems[x.dashboardId].isVisible = false;
        });
    }
}
